<script setup>
import { computed, nextTick, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAlert } from 'dashboard/composables';
import { useOnixKnowledgeStore } from 'dashboard/store/onix/knowledge';
import OnixPageLayout from 'dashboard/components-next/onix/OnixPageLayout.vue';
import KnowledgeEntryCard from 'dashboard/components-next/onix/KnowledgeEntryCard.vue';
import AddKnowledgeDialog from 'dashboard/components-next/onix/AddKnowledgeDialog.vue';
import AskKnowledgePanel from 'dashboard/components-next/onix/AskKnowledgePanel.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Policy from 'dashboard/components/policy.vue';

const { t } = useI18n();
const route = useRoute();
const { accountScopedRoute } = useAccount();
const knowledgeStore = useOnixKnowledgeStore();

const dialogRef = ref(null);
const dialogMode = ref('text');
const asked = ref(false);

const collectionId = computed(() => route.params.collectionId);
const current = computed(() => knowledgeStore.getCurrent);
const entries = computed(() => knowledgeStore.getEntries);
const results = computed(() => knowledgeStore.getRetrieveResults);
const uiFlags = computed(() => knowledgeStore.getUIFlags);
const backUrl = computed(() => accountScopedRoute('onix_knowledge_index'));

const load = () => knowledgeStore.fetchOne(collectionId.value);

const openDialog = mode => {
  dialogMode.value = mode;
  nextTick(() => dialogRef.value?.open());
};

const onSubmit = async payload => {
  const form = payload.file ? new FormData() : null;
  let data;
  if (form) {
    form.append('collection_id', collectionId.value);
    form.append('pdf_file', payload.file);
    form.append('source_type', 'document');
    data = await knowledgeStore.createChunk(form);
  } else {
    data = await knowledgeStore.createChunk({
      collection_id: collectionId.value,
      content: payload.content,
      source_type: 'manual',
    });
  }
  if (!data) {
    useAlert(t('ONIX.KNOWLEDGE.ERROR_CONTENT'));
    return;
  }
  dialogRef.value?.close();
  useAlert(t('ONIX.KNOWLEDGE.SAVED'));
  await load();
};

const ask = async question => {
  asked.value = true;
  const data = await knowledgeStore.retrieveTest({
    collectionId: collectionId.value,
    query: question,
    topK: 5,
  });
  if (data === null) useAlert(t('ONIX.KNOWLEDGE.ERROR_ASK'));
};

watch(
  collectionId,
  () => {
    asked.value = false;
    load();
  },
  { immediate: true }
);
</script>

<template>
  <OnixPageLayout
    :header-title="current?.name || t('ONIX.KNOWLEDGE.TITLE')"
    :description="t('ONIX.KNOWLEDGE.TOPIC_DESCRIPTION')"
    :back-url="backUrl"
    :is-fetching="uiFlags.fetchingItem && !current"
  >
    <template #actions>
      <Policy :permissions="['administrator']">
        <div class="flex gap-2">
          <Button
            :label="t('ONIX.KNOWLEDGE.WRITE')"
            icon="i-lucide-plus"
            size="sm"
            @click="openDialog('text')"
          />
          <Button
            :label="t('ONIX.KNOWLEDGE.UPLOAD')"
            color="slate"
            variant="faded"
            size="sm"
            @click="openDialog('pdf')"
          />
        </div>
      </Policy>
    </template>
    <template #body>
      <div class="flex flex-col gap-6 pb-8">
        <div
          v-if="!entries.length"
          class="rounded-xl bg-n-solid-2 outline outline-1 outline-n-container px-6 py-8 text-center"
        >
          <h2 class="text-base font-medium text-n-slate-12">
            {{ t('ONIX.KNOWLEDGE.ENTRIES_EMPTY_TITLE') }}
          </h2>
          <p class="mt-1 text-sm text-n-slate-11">
            {{ t('ONIX.KNOWLEDGE.ENTRIES_EMPTY_SUBTITLE') }}
          </p>
        </div>
        <div v-else class="flex flex-col gap-3">
          <KnowledgeEntryCard
            v-for="entry in entries"
            :key="entry.id"
            :excerpt="entry.excerpt"
            :origin="entry.origin"
            :state="entry.state"
          />
        </div>
        <AskKnowledgePanel
          :results="results"
          :is-loading="uiFlags.retrieving"
          :asked="asked"
          @ask="ask"
        />
      </div>
    </template>
  </OnixPageLayout>

  <AddKnowledgeDialog
    ref="dialogRef"
    :mode="dialogMode"
    :is-loading="uiFlags.creatingChunk"
    @submit="onSubmit"
  />
</template>
